/**
 * Per-chat composer manager (ELARA-952).
 *
 * The composer state used to be a single global {@link ChatInputState}. Typing a draft in one trip's
 * chat and switching to another leaked the draft — plus attachments, answered questions, sending
 * status, and errors — into the next chat, because everything lived on one shared object whose fields
 * were wiped by a `reset()` that raced the trip/chat switch (and behaved differently on mobile, where
 * the monitor is screen-scoped).
 *
 * This manager gives **each chat its own `ChatInputState` instance**, keyed by chat id in an in-memory
 * map. Isolation stops being a timing-dependent reset and becomes structural: an in-flight
 * `sendMessage()` on chat A mutates A's own signals, so there is no "wrong chat" to leak into,
 * regardless of when (or whether) any effect has run. Each instance also persists its serializable
 * draft per-chat, so an unsent draft survives an app reload.
 *
 * The manager is a **facade**: it exposes the whole `ChatInputState` surface and delegates each member
 * to the active instance (`current`), so every existing consumer — web via `useChatInputState()`,
 * mobile via `useAppStates().chatInputState.<member>` — keeps working unchanged. Delegation is by
 * explicit own members (not a Proxy) so that `this.current.<method>()` binds `this` to the concrete
 * instance (keeping an in-flight send correct across a switch) and so `jest.spyOn(chatInputState, …)`
 * still resolves a real spy. It additionally owns the cross-chat concerns: the guide seed (set before
 * any chat exists on `/trip/new`), the active-chat model sync, and instance lifecycle/eviction.
 *
 * **Call methods as `manager.method(...)`; do not detach them.** Each delegated method reads `this`
 * to reach `current`, so a *method* pulled off the facade and invoked standalone
 * (`const f = chatInputState.setMessage; f(...)`) loses its receiver and throws — bind it first
 * (`chatInputState.setMessage.bind(chatInputState)`) if you must detach. Destructuring *getters/values*
 * (`const { attachments } = chatInputState`) is fine: the getter runs at destructure time.
 */

import type { DataAttachmentType } from '@elara/domain/models/schemas/attachment-schemas';

import type { FilePicker } from '#lib/file-picker.js';
import type { ImageProcessor } from '#lib/image-processor.js';
import type { KeyValueStore } from '#lib/key-value-store.js';
import type { SpeechRecognitionPort } from '#lib/speech-recognition-port.js';
import type { ChatInputState, MessageScroller } from './ChatInputState.js';
import type { ChatsState } from './ChatsState.js';
import type { FileUploadState } from './FileUploadState.js';
import type { ModelState } from './ModelState.js';
import type { TripsState } from './TripsState.js';

import { effect, signal } from '@preact/signals-react';

import { createChatInputState } from './ChatInputState.js';

/**
 * Sentinel key for the id-less window (e.g. `/trip/new`) where no chat is selected yet. A `Symbol`
 * (not a string) so it can never collide with a real chat id, regardless of chat-id format.
 */
const NO_CHAT_KEY = Symbol('no-chat');

/** In-memory map key: a real chat id, or the {@link NO_CHAT_KEY} sentinel. */
type InstanceKey = string | typeof NO_CHAT_KEY;

/**
 * Upper bound on retained composer instances. A real traveler has a handful of chats; this is a guard
 * against a privileged user browsing many trips in one session, not a hot path. The least-recently
 * active instance is evicted first, never the active one or one with a send in flight.
 */
const MAX_INSTANCES = 8;

type GuideSeed = { type: DataAttachmentType; id: string; displayName?: string };

/** An attachment made before a chat existed, kept until one does. */
export type PendingAttachment =
  | { kind: 'data'; type: DataAttachmentType; id: string; displayName?: string; data?: Record<string, unknown> }
  | { kind: 'url'; url: string; mimeType: string; name: string; size?: number };

/**
 * The manager's public shape: the full {@link ChatInputState} surface (delegated to the active chat)
 * plus the cross-chat pieces the manager owns.
 */
export type ChatInputStateManager = ChatInputState & {
  /** The active chat's composer instance — the target the facade delegates to. */
  readonly current: ChatInputState;
  /**
   * Queue a guide entity to seed the first chat of the *next* trip. Set by the off-trip "Plan a trip"
   * CTA before navigating to `/trip/new`, where no chat id exists yet; applied once a chat is ready.
   */
  seedOnNextChat(type: DataAttachmentType, id: string, displayName?: string): void;
  /** The queued guide seed awaiting a chat, if any. */
  readonly pendingSeed: GuideSeed | undefined;
  /** Text awaiting a chat to be dropped, unsent, into its composer. */
  readonly pendingDraft: string | undefined;
  /**
   * Queue text to appear in the *next* available chat's composer, unsent, for the guest to finish.
   *
   * @example
   * chatInputState.queueDraft('Find somewhere to go in November.');
   * openTripSection(tripId, 'TripChat'); // arrives with the composer filled
   */
  queueDraft(text: string): void;
  /**
   * Queue a ready-to-send message for the next available chat — composed elsewhere and sent on arrival,
   * with any attachments queued alongside it.
   */
  queueMessage(message: string): void;
  /** The message awaiting a chat to send it, if any. */
  pendingMessage: string | undefined;
  /** Attachments awaiting a chat to receive them, oldest first. */
  readonly pendingAttachments: readonly PendingAttachment[];
};

export function createChatInputStateManager(
  chatsState: ChatsState,
  messageViewState: MessageScroller,
  fileUploadState: FileUploadState,
  tripsState: TripsState,
  modelState: ModelState,
  keyValueStore: KeyValueStore,
  filePicker: FilePicker,
  imageProcessor: ImageProcessor,
  speechRecognition: SpeechRecognitionPort,
): ChatInputStateManager {
  const instances = new Map<InstanceKey, ChatInputState>();
  /** Keys ordered least- to most-recently active, for LRU eviction. */
  const lruOrder: InstanceKey[] = [];
  const pendingSeed = signal<GuideSeed | undefined>(undefined);
  /**
   * Text waiting for a chat to exist, to be placed in its composer unsent.
   *
   * Manager-scoped for the same reason as {@link pendingSeed}: it is queued from screens that have no
   * chat yet (a cold trip's overview) or whose chat is mid-switch, so a per-instance home would strand
   * it on a composer the guest never opens.
   */
  const pendingDraft = signal<string | undefined>(undefined);
  /**
   * Data attachments waiting for a chat to exist.
   *
   * Manager-scoped for the same reason as {@link pendingDraft}. Every caller of `addDataAttachment` is a
   * guide's "Ask Elara", which attaches the entity and then navigates to the chat — so it runs from a
   * screen that is NOT the chat, on a trip whose chat may still be resolving. Held on `current`, the
   * attachment landed on the id-less instance and was gone by the time the guest arrived.
   *
   * A list, not one slot: two guides can be opened and asked about before the chat resolves.
   */
  const pendingAttachments = signal<PendingAttachment[]>([]);
  /**
   * A ready-to-send message waiting for a chat to exist.
   *
   * Manager-scoped for the same reason as the rest: its callers are guide actions that compose a
   * request and then navigate to the chat, so they run before the chat is necessarily current.
   */
  const pendingMessage = signal<string | undefined>(undefined);
  let previousKey: InstanceKey | undefined;
  let monitorCleanup: (() => void) | null = null;

  /** The key for the currently-selected chat (or the sentinel when none is selected). */
  function keyFor(): InstanceKey {
    return chatsState.current?.id ?? NO_CHAT_KEY;
  }

  function touch(key: InstanceKey): void {
    // Fast path for the hot path: nearly every facade read routes through `current` → `getOrCreate` →
    // `touch`, but the active key rarely changes between reads. Skip the splice/push churn when `key`
    // is already the most-recently-active entry, so a plain `manager.message` read costs nothing here.
    if (lruOrder[lruOrder.length - 1] === key) return;
    const index = lruOrder.indexOf(key);
    if (index !== -1) lruOrder.splice(index, 1);
    lruOrder.push(key);
  }

  function getOrCreate(key: InstanceKey): ChatInputState {
    let instance = instances.get(key);
    if (!instance) {
      const chatId = typeof key === 'string' ? key : undefined;
      instance = createChatInputState(
        chatId,
        chatsState,
        messageViewState,
        fileUploadState,
        tripsState,
        modelState,
        keyValueStore,
        filePicker,
        imageProcessor,
        speechRecognition,
      );
      instances.set(key, instance);
      lruOrder.push(key);
    }
    touch(key);
    return instance;
  }

  /**
   * Evict least-recently-active instances beyond the cap, never the active one or one mid-send. This
   * runs only from the manager's switch effect, so the cap is soft: while monitors are stopped (e.g.
   * between mobile screens) `getOrCreate` can grow the map past `MAX_INSTANCES`, and it is trimmed
   * again on the next switch once monitors resume.
   */
  function evictIfNeeded(activeKey: InstanceKey): void {
    while (instances.size > MAX_INSTANCES) {
      const victim = lruOrder.find((key) => key !== activeKey && !instances.get(key)?.isSending);
      if (!victim) break; // everything remaining is active or in-flight — leave it.
      instances.get(victim)?.dispose();
      instances.delete(victim);
      lruOrder.splice(lruOrder.indexOf(victim), 1);
    }
  }

  const facade: ChatInputStateManager = {
    // ---- manager-owned cross-chat state ----
    /** Get-or-create the active chat's instance. Reads reactively via `chatsState.current`. */
    get current() {
      return getOrCreate(keyFor());
    },
    get pendingSeed() {
      return pendingSeed.value;
    },
    get pendingDraft() {
      return pendingDraft.value;
    },
    get pendingMessage() {
      return pendingMessage.value;
    },
    get pendingAttachments() {
      return pendingAttachments.value;
    },
    set pendingMessage(value) {
      pendingMessage.value = value;
    },
    seedOnNextChat(type, id, displayName) {
      pendingSeed.value = { type, id, displayName };
    },

    // ---- delegated value signals (read + write) ----
    get message() {
      return this.current.message;
    },
    set message(value) {
      this.current.message = value;
    },
    get attachments() {
      return this.current.attachments;
    },
    set attachments(value) {
      this.current.attachments = value;
    },
    get answeredQuestions() {
      return this.current.answeredQuestions;
    },
    set answeredQuestions(value) {
      this.current.answeredQuestions = value;
    },
    get isSending() {
      return this.current.isSending;
    },
    set isSending(value) {
      this.current.isSending = value;
    },
    get isDragging() {
      return this.current.isDragging;
    },
    set isDragging(value) {
      this.current.isDragging = value;
    },
    get isTyping() {
      return this.current.isTyping;
    },
    set isTyping(value) {
      this.current.isTyping = value;
    },
    get isVoiceInputActive() {
      return this.current.isVoiceInputActive;
    },
    set isVoiceInputActive(value) {
      this.current.isVoiceInputActive = value;
    },
    get pendingVoiceText() {
      return this.current.pendingVoiceText;
    },
    set pendingVoiceText(value) {
      this.current.pendingVoiceText = value;
    },
    get placeholderIndex() {
      return this.current.placeholderIndex;
    },
    set placeholderIndex(value) {
      this.current.placeholderIndex = value;
    },
    get sendError() {
      return this.current.sendError;
    },
    set sendError(value) {
      this.current.sendError = value;
    },
    get _session() {
      return this.current._session;
    },
    set _session(value) {
      this.current._session = value;
    },

    // ---- delegated computeds (read-only) ----
    get supportsVoiceInput() {
      return this.current.supportsVoiceInput;
    },
    get currentChat() {
      return this.current.currentChat;
    },
    get isDemoTrip() {
      return this.current.isDemoTrip;
    },
    get isInputDisabled() {
      return this.current.isInputDisabled;
    },
    get isChatEmpty() {
      return this.current.isChatEmpty;
    },
    get placeholder() {
      return this.current.placeholder;
    },
    get displayText() {
      return this.current.displayText;
    },
    get canSend() {
      return this.current.canSend;
    },
    get hasUnsavedChanges() {
      return this.current.hasUnsavedChanges;
    },
    get totalAttachmentSize() {
      return this.current.totalAttachmentSize;
    },
    get attachmentsExceedLimit() {
      return this.current.attachmentsExceedLimit;
    },
    get formattedAttachmentSize() {
      return this.current.formattedAttachmentSize;
    },
    get questions() {
      return this.current.questions;
    },
    get hasAnsweredQuestions() {
      return this.current.hasAnsweredQuestions;
    },

    // ---- delegated methods (each binds `this` to the concrete instance) ----
    setMessage(text) {
      this.current.setMessage(text);
    },
    addAttachments(assets) {
      this.current.addAttachments(assets);
    },
    pickFiles() {
      return this.current.pickFiles();
    },
    addUrlAttachment(url, mimeType, name, size) {
      // Queued when there is no chat yet, like a data attachment — its callers are the "Ask Elara about
      // this image" actions on a guide's lightbox and an itinerary event, which attach and then navigate.
      if (keyFor() === NO_CHAT_KEY) {
        pendingAttachments.value = [...pendingAttachments.value, { kind: 'url', url, mimeType, name, size }];
        return;
      }
      this.current.addUrlAttachment(url, mimeType, name, size);
    },
    addDataAttachment(type, id, displayName, data) {
      // Queue rather than lose it when there is no chat yet — see `pendingAttachments`. With a chat
      // already current this is the plain delegation it has always been.
      if (keyFor() === NO_CHAT_KEY) {
        pendingAttachments.value = [...pendingAttachments.value, { kind: 'data', type, id, displayName, data }];
        return;
      }
      this.current.addDataAttachment(type, id, displayName, data);
    },
    queueMessage(message: string) {
      // Held here, not on `current` — same reason as `queueDraft`. Applied AFTER any queued attachments
      // so they go with the message rather than arriving to an already-sent one.
      pendingMessage.value = message;
    },
    queueDraft(text: string) {
      // Held by the MANAGER, not delegated to `current` — the bug this fixes. Composer instances are
      // keyed by chat id, and a prompt is nearly always tapped before the target chat is current: on a
      // cold trip there is no chat yet, and switching trips leaves the previous one current for a beat.
      // The draft then landed on an instance the guest never types in, and the composer they arrived at
      // was empty — intermittently, depending only on whether the chat happened to be current already.
      // `pendingSeed` above is held here for exactly this reason.
      pendingDraft.value = text;
    },
    removeAttachment(index) {
      this.current.removeAttachment(index);
    },
    clearAttachments() {
      this.current.clearAttachments();
    },
    setDragging(isDragging) {
      this.current.setDragging(isDragging);
    },
    clearInput() {
      this.current.clearInput();
    },
    restoreEarlyStoppedDraft(fallbackText) {
      this.current.restoreEarlyStoppedDraft(fallbackText);
    },
    reset() {
      this.current.reset();
    },
    sendMessage() {
      return this.current.sendMessage();
    },
    handleEnterKey(event) {
      return this.current.handleEnterKey(event);
    },
    formatFileSize(bytes) {
      return this.current.formatFileSize(bytes);
    },
    registerTextareaRef(ref) {
      this.current.registerTextareaRef(ref);
    },
    focusInput() {
      this.current.focusInput();
    },
    startVoiceInput() {
      this.current.startVoiceInput();
    },
    stopVoiceInput() {
      this.current.stopVoiceInput();
    },
    toggleVoiceInput() {
      this.current.toggleVoiceInput();
    },
    answerQuestion(args) {
      this.current.answerQuestion(args);
    },
    toggleAnswer(args) {
      this.current.toggleAnswer(args);
    },
    isAnswerSelected(question, answer) {
      return this.current.isAnswerSelected(question, answer);
    },
    getAnswer(question) {
      return this.current.getAnswer(question);
    },
    removeAnswer(question) {
      this.current.removeAnswer(question);
    },
    clearAllAnswers() {
      this.current.clearAllAnswers();
    },
    startPlaceholderCycle() {
      this.current.startPlaceholderCycle();
    },
    stopPlaceholderCycle() {
      this.current.stopPlaceholderCycle();
    },
    update(updates) {
      this.current.update(updates);
    },

    // ---- manager lifecycle ----
    /**
     * Start the switch monitor: swap monitors between instances on chat change, sync the model for the
     * active chat, apply a queued guide seed once a chat is ready, and evict stale instances.
     */
    startMonitors() {
      if (monitorCleanup) return monitorCleanup;

      const cleanup = effect(() => {
        const key = keyFor();
        const currentChatId = chatsState.current?.id;

        if (previousKey === undefined) {
          // Initial pass: activate the current chat's composer.
          getOrCreate(key).startMonitors();
        } else if (previousKey !== key) {
          const outgoing = instances.get(previousKey);
          const incoming = getOrCreate(key);
          // Hand a draft typed during the id-less `/trip/new` window off to the first real chat once,
          // so it isn't stranded on the sentinel instance. This is currently unreachable: no off-trip
          // composer writes the sentinel's message/attachments today (TripPage and the mobile screens
          // only mount the composer once a chat exists; the "Plan a trip" CTA uses `seedOnNextChat`,
          // not `message`). It's kept as the correct behavior for the `/trip/new` → first-chat flow it
          // is built for. If an off-trip composer is ever added, note the assumption: this hands the
          // draft to whichever chat becomes current *next* — verify that is the intended target before
          // relying on it, or it could hand a draft to an unrelated chat.
          if (previousKey === NO_CHAT_KEY && outgoing && key !== NO_CHAT_KEY && outgoing.hasUnsavedChanges) {
            incoming.message = outgoing.message;
            incoming.attachments = [...outgoing.attachments];
            incoming.answeredQuestions = { ...outgoing.answeredQuestions };
            outgoing.reset();
          }
          outgoing?.stopMonitors();
          incoming.startMonitors();
        }

        // Model override is chat-scoped; keep it synced to the active chat (idempotent when unchanged).
        modelState.setCurrentChat(currentChatId ?? '');

        // Apply a queued draft once a chat exists: text the guest is expected to finish, so it stops in
        // the composer rather than sending. Ordered before the seed, which sends — a seed arriving in
        // the same pass should not have its message overwritten by a draft.
        if (pendingDraft.value !== undefined && currentChatId) {
          const draft = pendingDraft.value;
          pendingDraft.value = undefined;
          getOrCreate(key).message = draft;
        }

        // Apply any attachments queued before a chat existed, in the order they were made.
        if (pendingAttachments.value.length > 0 && currentChatId) {
          const queued = pendingAttachments.value;
          pendingAttachments.value = [];
          const active = getOrCreate(key);
          for (const attachment of queued) {
            if (attachment.kind === 'data') {
              active.addDataAttachment(attachment.type, attachment.id, attachment.displayName, attachment.data);
            } else {
              active.addUrlAttachment(attachment.url, attachment.mimeType, attachment.name, attachment.size);
            }
          }
        }

        // A queued SEND is applied last: attachments are already on the composer, and it is terminal.
        if (pendingMessage.value !== undefined && currentChatId) {
          const message = pendingMessage.value;
          pendingMessage.value = undefined;
          const active = getOrCreate(key);
          active.message = message;
          void active.sendMessage();
        }

        // Apply a queued guide seed once a chat exists — compose and send it as the trip's first
        // message so Elara replies with planning questions rather than leaving a pre-filled composer.
        if (pendingSeed.value && currentChatId) {
          const seed = pendingSeed.value;
          pendingSeed.value = undefined;
          const active = getOrCreate(key);
          const name = seed.displayName?.trim();
          active.message = name ? `Let's plan a trip to ${name}` : `Let's plan a trip`;
          active.addDataAttachment(seed.type, seed.id, seed.displayName);
          void active.sendMessage();
        }

        touch(key);
        evictIfNeeded(key);
        previousKey = key;
      });

      // Idempotent teardown: the first invocation clears `monitorCleanup` and does the work, so
      // calling the returned closure directly AND calling `stopMonitors()` — as `TripChatScreen` does
      // on unmount (`stop?.(); chatInputState.stopMonitors()`) — tears down exactly once instead of
      // disposing the effect and looping every instance's `stopMonitors()` twice.
      let disposed = false;
      const teardown = () => {
        if (disposed) return;
        disposed = true;
        monitorCleanup = null;
        cleanup();
        for (const instance of instances.values()) instance.stopMonitors();
        previousKey = undefined;
      };
      monitorCleanup = teardown;
      return teardown;
    },

    /** Stop the switch monitor and every instance's monitors (instances stay cached for reuse). */
    stopMonitors() {
      monitorCleanup?.();
    },

    /** Tear down every instance (used on full teardown). Drafts are already persisted per-chat. */
    dispose() {
      this.stopMonitors();
      for (const instance of instances.values()) instance.dispose();
      instances.clear();
      lruOrder.length = 0;
    },
  };

  return facade;
}
